import React, { forwardRef } from 'react';
import {
  View,
  Image as RNImage,
  Text,
  StyleSheet,
  ViewStyle,
  ImageSourcePropType,
} from 'react-native';
import { Image } from 'expo-image';
import TrailPathPreview from '@/components/TrailPathPreview';
import TrailMapPreview from '@/components/TrailMapPreview';
import { Coordinate } from '@/types/trail';
import { theme } from '@/constants/colors';
import { useTranslation } from 'react-i18next';

export type ShareFormat =
  | 'share_1'
  | 'share_2'
  | 'share_3'
  | 'share_4'
  | 'share_5'
  | 'share_6';

/** @deprecated Use ShareFormat instead */
export type GradientStyle = ShareFormat;

export type StatConfig = {
  value: string;
  label?: string;
  unit?: string;
};

/** Default logo SVG – used when no custom logoImage prop is provided. */
import DEFAULT_LOGO from '../assets/images/logo-white.svg';
import COLOR_LOGO from '../assets/images/logo.svg';
import LOGO_WHITE_TEXT_BELOW from '../assets/images/share-icons/logo-white-text-below.svg';
import TEXT_WHITE from '../assets/images/share-icons/text-white.svg';
import LOGO_WHITE_TEXT_RIGHT from '../assets/images/share-icons/logo-white-text-right.svg';
import LOGO_GREEN_TEXT_RIGHT from '../assets/images/share-icons/logo-green-text-right.svg';
import PAW_BACKGROUND from '../assets/images/share-icons/orange-paw-background.svg';


export interface TrailSharePreviewProps {
  /** Which of the 6 share overlays to render */
  format: ShareFormat;

  /** The full-bleed background image (e.g. the photo of the dog) */
  backgroundImage: ImageSourcePropType;

  /** Trail coordinates – rendered as an inline SVG path when no routeOutlineImage */
  coordinates?: Coordinate[];

  /** Trail path array – used as fallback when coordinates is empty */
  path?: number[][] | null;

  /** Optional pre-rendered route-outline image (overrides inline SVG) */
  routeOutlineImage?: ImageSourcePropType;

  /** Optional full map tile used in Share_6 (overrides inline SVG) */
  mapImage?: ImageSourcePropType;

  /** Optional custom logo asset. Falls back to the built-in white SVG logo. */
  logoImage?: ImageSourcePropType;

  /**
   * When true, the background image is hidden and the container bg becomes
   * transparent – used when capturing a share image that should have no
   * background (the caller can composite it later). When false (default),
   * the background image renders normally.
   */
  transparentBackground?: boolean;

  /** Activity stats */
  distance?: StatConfig;
  duration?: StatConfig;
  pace?: StatConfig;

  /** Container style overrides */
  style?: ViewStyle;
}

/* ── Shared text-shadow for white text over photos ── */
const SHADOW = {
  textShadowColor: 'rgba(0, 0, 0, 0.6)',
  textShadowOffset: { width: 0, height: 1 },
  textShadowRadius: 3,
} as const;

export const TrailSharePreview = forwardRef<View, TrailSharePreviewProps>(({
  format,
  backgroundImage,
  coordinates,
  path,
  routeOutlineImage,
  mapImage,
  logoImage,
  transparentBackground = false,
  distance,
  duration,
  pace,
  style,
}, ref) => {
  const { t: t18n } = useTranslation();
  const hasPathData = (coordinates && coordinates.length > 0) || (path && path.length > 0);

  const d = {
    label: distance?.label ?? 'DISTANCE',
    value: distance?.value ?? '--',
    unit: distance?.unit ?? 'KM',
  };

  const t = {
    label: duration?.label ?? 'DURATION',
    value: duration?.value ?? '--',
    unit: duration?.unit ?? 'TIME',
  };

  const p = pace
    ? {
        label: pace.label ?? 'PACE',
        value: pace.value,
        unit: pace.unit ?? 'MIN/KM',
      }
    : null;

  const logoSource = logoImage ?? DEFAULT_LOGO;

  /**
   * Render the Tail Trails logo.
   * Uses the built-in white SVG logo by default, or a custom logoImage if provided.
   * Pass `overrideSource` to swap in an alternate built-in logo (e.g. the full-color one).
   */
  const renderLogo = (logoStyle: ViewStyle, overrideSource?: any) => {
    const src = overrideSource ?? logoSource;
    // Built-in logos are svg-transformer components — they size by their
    // baked-in width/height props, NOT style, so an unwrapped Svg renders at
    // intrinsic size and bleeds over neighbouring elements. Wrap in a View of
    // the style box and fill it (same pattern TrailPathPreview uses).
    if (typeof src === 'function' || src?.$$typeof != null) {
      const Svg = src;
      return (
        <View style={logoStyle as any}>
          <Svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" />
        </View>
      );
    }
    return <Image source={src} style={[logoStyle] as any} contentFit="contain" />;
  };

  /**
   * Render the trail route outline.
   * 1. If a pre-rendered `routeOutlineImage` is provided, use that.
   * 2. Else if coordinates/path data exists, render the SVG inline.
   * 3. Else show a placeholder.
   */
  const renderRouteOutline = (sizeStyle: ViewStyle, strokeColor = '#fff', strokeWidth = 2, bgColor = 'transparent') =>
    routeOutlineImage ? (
      <RNImage
        source={routeOutlineImage}
        style={[sizeStyle, { tintColor: strokeColor, SHADOW }] as any}
        resizeMode="contain"
      />
    ) : hasPathData ? (
      <TrailPathPreview
        coordinates={coordinates}
        path={path}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
        backgroundColor={bgColor}
        style={sizeStyle}
      />
    ) : (
      <View style={[sizeStyle, styles.routePlaceholder]} />
    );

  /* ────────────────────────────────────────────
     CHECKERED – transparency-indicator background,
     centred stat stack + logo (new default)
     ──────────────────────────────────────────── */
  /* ────────────────────────────────────────────
     SHARE 1 – Three-column header + bottom-center stack
     ──────────────────────────────────────────── */
  const renderShare1 = () => (
    <>
      {/* Top stats row */}
      <View style={styles.share1Header}>
        <View style={styles.statColumnCenter}>
          <Text style={styles.share1Label}>{d.label}</Text>
          <Text style={styles.share1Value}>{d.value}</Text>
          <Text style={styles.share1Unit}>{d.unit}</Text>
        </View>
        <View style={styles.statColumnCenter}>
          <Text style={styles.share1Label}>{t.label}</Text>
          <Text style={styles.share1Value}>{t.value}</Text>
          <Text style={styles.share1Unit}>{t.unit}</Text>
        </View>
        {p && (
          <View style={styles.statColumnCenter}>
            <Text style={styles.share1Label}>{p.label}</Text>
            <Text style={styles.share1Value}>{p.value}</Text>
            <Text style={styles.share1Unit}>{p.unit}</Text>
          </View>
        )}
      </View>

      {/* Bottom-center route outline + logo stack */}
      <View style={styles.share1BottomCenter}>
        {renderRouteOutline(styles.share1Route, '#fff', 2.5, 'transparent')}
        {renderLogo(styles.share1Logo, LOGO_WHITE_TEXT_BELOW)}
      </View>
    </>
  );

  /* ────────────────────────────────────────────
     SHARE 2 – Top-centered stats stack + center logo
     ──────────────────────────────────────────── */
  const renderShare2 = () => (
    <>
      {/* Top stats stack */}
      <View style={styles.share2Header}>
        <View style={styles.share2StatBlock}>
          <Text style={styles.share2Label}>{d.label}</Text>
          <Text style={styles.share2Value}>{d.value}</Text>
          <Text style={styles.share2Unit}>{d.unit}</Text>
        </View>
        <View style={styles.share2StatBlock}>
          <Text style={styles.share2Label}>{t.label}</Text>
          <Text style={styles.share2Value}>{t.value}</Text>
          <Text style={styles.share2Unit}>{t.unit}</Text>
        </View>
        {p && (
          <View style={styles.share2StatBlock}>
            <Text style={styles.share2Label}>{p.label}</Text>
            <Text style={styles.share2Value}>{p.value}</Text>
            <Text style={styles.share2Unit}>{p.unit}</Text>
          </View>
        )}
      </View>

      {/* Center logo */}
      <View style={styles.share2LogoCenter}>
        {renderLogo(styles.share2LogoImg, LOGO_WHITE_TEXT_BELOW)}
      </View>
    </>
  );

  /* ────────────────────────────────────────────
     SHARE 3 – Split left-stats / right-(route + logo)
     ──────────────────────────────────────────── */
  const renderShare3 = () => (
    <>
      {/* Top split: stats left, route outline + logo right */}
      <View style={styles.share3TopOverlay}>
        <View style={styles.share3StatsColumn}>
          <View style={styles.share3StatRow}>
            <Text style={styles.share3Label}>{d.label}</Text>
            <View style={styles.share3ValueRow}>
              <Text style={styles.share3Value}>{d.value}</Text>
              <Text style={styles.share3Unit}>{d.unit}</Text>
            </View>
          </View>
          <View style={styles.share3StatRow}>
            <Text style={styles.share3Label}>{t.label}</Text>
            <View style={styles.share3ValueRow}>
              <Text style={styles.share3Value}>{t.value}</Text>
              <Text style={styles.share3Unit}>{t.unit}</Text>
            </View>
          </View>
          {p && (
            <View style={styles.share3StatRow}>
              <Text style={styles.share3Label}>{p.label}</Text>
              <View style={styles.share3ValueRow}>
                <Text style={styles.share3Value}>{p.value}</Text>
                <Text style={styles.share3Unit}>{p.unit}</Text>
              </View>
            </View>
          )}
        </View>

        <View style={styles.share3RightColumn}>
          {renderRouteOutline(styles.share3Route, '#fff', 2.5, 'transparent')}
          {renderLogo(styles.share3LogoImg, LOGO_WHITE_TEXT_BELOW)}
        </View>
      </View>
    </>
  );

  /* ────────────────────────────────────────────
     SHARE 4 – Square quadrant overlay
     ──────────────────────────────────────────── */
  const renderShare4 = () => (
    <>
      <View style={styles.share4Square}>
        {/* Top row */}
        <View style={styles.share4TopRow}>
          <View style={styles.share4TopLeft}>
            {renderLogo(styles.share4LogoImg)}
          </View>
          <View style={styles.share4TopRight}>
            {renderLogo(styles.share4LogoImg, TEXT_WHITE)}
          </View>
        </View>

        {/* Bottom row */}
        <View style={styles.share4BottomRow}>
          <View style={styles.share4BottomLeft}>
            <Text style={styles.share4Label}>{d.label}</Text>
            <Text style={styles.share4Value}>{d.value}</Text>
            <Text style={styles.share4Unit}>{d.unit}</Text>
          </View>
          <View style={styles.share4BottomRight}>
            <Text style={styles.share4Label}>{t.label}</Text>
            <Text style={styles.share4Value}>{t.value}</Text>
            <Text style={styles.share4Unit}>{t.unit}</Text>
          </View>
        </View>
      </View>
    </>
  );

  /* ────────────────────────────────────────────
     SHARE 5 – Centered square picture-in-picture
     ──────────────────────────────────────────── */
  const renderShare5 = () => (
    <>
      {/* Heavily zoomed background */}
      {!transparentBackground && (
        <Image
          source={backgroundImage}
          style={styles.share5ZoomBackground}
          contentFit="cover"
        />
      )}
      {/* Dark scrim over the zoom */}
      {!transparentBackground && <View style={styles.share5ZoomScrim} />}
      {/* Floating square card */}
      <View style={styles.share5Card}>
        {!transparentBackground ? (
          <Image
            source={backgroundImage}
            style={styles.share5CardBackground}
            contentFit="cover"
          />
        ) : (
          <View style={[styles.share5CardBackground, { backgroundColor: 'transparent' }]} />
        )}
        <View style={styles.share5CardOverlay}>
          <View style={styles.share5CardTopRight}>
            {renderLogo(styles.share5LogoImg, LOGO_WHITE_TEXT_RIGHT)}
          </View>
          {/* Bottom bar: stats stacked like other templates */}
          <View style={styles.share5CardBottomBar}>
            <View style={styles.share5Stat}>
              <Text style={styles.share5Label}>{d.label}</Text>
              <Text style={styles.share5Value}>{d.value}</Text>
              <Text style={styles.share5Unit}>{d.unit}</Text>
            </View>
            <View style={styles.share5Stat}>
              <Text style={styles.share5Label}>{t.label}</Text>
              <Text style={styles.share5Value}>{t.value}</Text>
              <Text style={styles.share5Unit}>{t.unit}</Text>
            </View>
          </View>
        </View>
      </View>
    </>
  );

  /* ────────────────────────────────────────────
     SHARE 6 – Dark split map card
     ──────────────────────────────────────────── */
  const renderShare6 = () => (
    <>
      {/* Full dark overlay */}
      <View style={styles.share6Overlay}>
        <View style={styles.share6PawBackground}>
          <PAW_BACKGROUND width="100%" height="100%" preserveAspectRatio="xMidYMid slice" />
        </View>
        <View style={styles.share6Top}>
          {renderLogo(styles.share6LogoImg, LOGO_GREEN_TEXT_RIGHT)}
        </View>

        <View style={styles.share6MapWrapper}>
          {mapImage ? (
            <RNImage
              source={mapImage}
              style={styles.share6MapImage}
              resizeMode="cover"
            />
          ) : hasPathData ? (
            <TrailMapPreview
              coordinates={coordinates}
              path={path}
              style={styles.share6MapImage}
              showOnlyPath={false}
            />
          ) : (
            <View style={styles.share6MapPlaceholder}>
              <Text style={styles.share6MapPlaceholderText}>{t18n('explore.mapView')}</Text>
            </View>
          )}
        </View>

        <View style={styles.share6BottomBar}>
          <View style={styles.share6Stat}>
            <Text style={styles.share6Label}>{d.label}</Text>
            <Text style={styles.share6Value}>{d.value}</Text>
            <Text style={styles.share6Unit}>{d.unit}</Text>
          </View>
          <View style={styles.share6Stat}>
            <Text style={styles.share6Label}>{t.label}</Text>
            <Text style={styles.share6Value}>{t.value}</Text>
            <Text style={styles.share6Unit}>{t.unit}</Text>
          </View>
          {p && (
            <View style={styles.share6Stat}>
              <Text style={styles.share6Label}>{p.label}</Text>
              <Text style={styles.share6Value}>{p.value}</Text>
              <Text style={styles.share6Unit}>{p.unit}</Text>
            </View>
          )}
        </View>
      </View>
    </>
  );

  const renderOverlay = () => {
    switch (format) {
      case 'share_1':
        return renderShare1();
      case 'share_2':
        return renderShare2();
      case 'share_3':
        return renderShare3();
      case 'share_4':
        return renderShare4();
      case 'share_5':
        return renderShare5();
      case 'share_6':
        return renderShare6();
      default:
        return renderShare1();
    }
  };

  return (
    <View ref={ref} style={[styles.container, transparentBackground && styles.containerTransparent, style]}>
      {!transparentBackground && (
        <Image
          source={backgroundImage}
          style={styles.background}
          contentFit="cover"
        />
      )}
      {renderOverlay()}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    width: '100%',
    aspectRatio: 9 / 16,
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: '#000',
  },
  containerTransparent: {
    backgroundColor: 'transparent',
  },
  background: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
  },

  // Route outline placeholder
  routePlaceholder: {
    borderWidth: 2,
    borderColor: '#fff',
    borderRadius: 12,
    backgroundColor: 'transparent',
  },

  // ══════════════════════════════════════════
  // SHARE 1 – Three-column header + bottom-center stack
  // ══════════════════════════════════════════
  share1Header: {
    position: 'absolute',
    top: 120,
    left: 0,
    right: 0,
    backgroundColor: 'transparent',
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  statColumnCenter: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  share1Label: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '600',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginBottom: 2,
    ...SHADOW,
  },
  share1Value: {
    color: '#fff',
    fontSize: 24,
    fontWeight: '700',
    ...SHADOW,
  },
  share1Unit: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 10,
    fontWeight: '500',
    marginTop: 1,
    ...SHADOW,
  },
  share1BottomCenter: {
    position: 'absolute',
    bottom: 140,
    left: 0,
    right: 0,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Route 140 + logo 60 + bottom 140 keeps the stack clear of the stats
  // row (header ends ~y175) at the 312×554.7 design canvas.
  share1Route: {
    width: 140,
    height: 140,
  },
  share1Logo: {
    width: 120,
    height: 60,
  },

  // ══════════════════════════════════════════
  // SHARE 2 – Top-centered stats stack + center logo
  // ══════════════════════════════════════════
  share2Header: {
    position: 'absolute',
    top: 60,
    left: 0,
    right: 0,
    backgroundColor: 'transparent',
    flexDirection: 'column',
    alignItems: 'center',
  },
  share2StatBlock: {
    alignItems: 'center',
    marginVertical: 4,
  },
  share2Label: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '600',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    ...SHADOW,
  },
  share2Value: {
    color: '#fff',
    fontSize: 30,
    fontWeight: '700',
    marginVertical: 1,
    ...SHADOW,
  },
  share2Unit: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 11,
    fontWeight: '500',
    ...SHADOW,
  },
  share2LogoCenter: {
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    alignItems: 'center',
    transform: [{ translateY: -24 }],
  },
  share2LogoImg: {
    width: 110,
    height: 55,
    marginTop: 40,
  },

  // ══════════════════════════════════════════
  // SHARE 3 – Split left-stats / right-(route + logo)
  // ══════════════════════════════════════════
  share3TopOverlay: {
    position: 'absolute',
    top: 60,
    left: 24,
    right: 24,
    backgroundColor: 'transparent',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  share3StatsColumn: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  share3StatRow: {
    marginVertical: 6,
  },
  share3Label: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '600',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginBottom: 2,
    ...SHADOW,
  },
  share3ValueRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  share3Value: {
    color: '#fff',
    fontSize: 28,
    fontWeight: '700',
    marginRight: 6,
    ...SHADOW,
  },
  share3Unit: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 11,
    fontWeight: '500',
    marginBottom: 4,
    ...SHADOW,
  },
  share3RightColumn: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  share3Route: {
    width: 120,
    height: 120,
  },
  share3LogoImg: {
    width: 120,
    height: 40,
    marginTop: 12,
  },

  // ══════════════════════════════════════════
  // SHARE 4 – Square quadrant overlay
  // ══════════════════════════════════════════
  share4Square: {
    position: 'absolute',
    alignSelf: 'center',
    top: '10%',
    width: '88%',
    aspectRatio: 1,
    borderRadius: 12,
    overflow: 'hidden',
  },
  share4TopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flex: 1,
    paddingHorizontal: 30,
    paddingTop: 30,
    gap: 16,
  },
  share4TopLeft: {
    flex: 1,
  },
  share4LogoImg: {
    width: 80,
    height: 60,
  },
  share4TopRight: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  share4BrandName: {
    color: '#fff',
    fontSize: 20,
    fontWeight: '700',
    letterSpacing: 1.5,
    ...SHADOW,
  },
  share4BottomRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    flex: 1,
    paddingHorizontal: 30,
    paddingBottom: 30,
  },
  share4BottomLeft: {
    flexShrink: 0,
  },
  share4BottomRight: {
    flexShrink: 0,
    alignItems: 'flex-end',
  },
  share4Label: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '600',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 4,
    ...SHADOW,
  },
  share4Value: {
    color: '#fff',
    fontSize: 28,
    fontWeight: '700',
    ...SHADOW,
  },
  share4Unit: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 11,
    fontWeight: '500',
    marginTop: 2,
    ...SHADOW,
  },

  // ══════════════════════════════════════════
  // SHARE 5 – Centered square picture-in-picture
  // ══════════════════════════════════════════
  share5ZoomBackground: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
    transform: [{ scale: 3 }],
  },
  share5ZoomScrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
  },
  share5Card: {
    position: 'absolute',
    top: '25%',
    alignSelf: 'center',
    width: '70%',
    aspectRatio: 1,
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: '#000',
  },
  share5CardBackground: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
  },
  share5CardOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'space-between',
    padding: 16,
  },
  share5CardTopRight: {
    alignSelf: 'flex-end',
    paddingRight: 10,
  },
  share5LogoImg: {
    marginTop: 10,
    width: 80,
    height: 26,
  },
  share5CardBottomBar: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'flex-start',
    backgroundColor: 'transparent',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  share5Stat: {
    alignItems: 'center',
  },
  share5Label: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '600',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 2,
    ...SHADOW,
  },
  share5Value: {
    color: '#fff',
    fontSize: 20,
    fontWeight: '700',
    ...SHADOW,
  },
  share5Unit: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 10,
    fontWeight: '500',
    marginTop: 1,
    ...SHADOW,
  },

  // ══════════════════════════════════════════
  // SHARE 6 – Dark base + map card + gold metrics
  // ══════════════════════════════════════════
  share6Overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: theme.backgroundPrimary,
  },
  share6PawBackground: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
    opacity: 0.9,
  },
  share6Top: {
    position: 'absolute',
    top: 40,
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  share6LogoImg: {
    width: 130,
    height: 42,
  },
  share6MapWrapper: {
    position: 'absolute',
    top: '20%',
    alignSelf: 'center',
    width: '88%',
    aspectRatio: 1,
    borderRadius: 4,
    overflow: 'hidden',
  },
  share6MapImage: {
    width: '100%',
    height: '100%',
  },
  share6MapPlaceholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  share6MapPlaceholderText: {
    color: theme.accentPrimary,
    fontSize: 16,
    fontWeight: '700',
  },
  share6BottomBar: {
    position: 'absolute',
    bottom: 40,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  share6Stat: {
    alignItems: 'center',
  },
  share6Label: {
    color: theme.accentPrimary,
    fontSize: 10,
    fontWeight: '600',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  share6Value: {
    color: theme.accentPrimary,
    fontSize: 24,
    fontWeight: '700',
    marginVertical: 2,
  },
  share6Unit: {
    color: theme.accentPrimary,
    fontSize: 10,
    fontWeight: '500',
  },
});

export default TrailSharePreview;